// context/OnboardingContext.js
import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import axios from '../axiosConfig';
import { OwnerContext } from './OwnerContext';
import i18n from '../i18n';

const OnboardingContext = createContext();

// Local storage key for persisting tour state during a session
const ONBOARDING_STATE_KEY = 'owner_onboarding_state';

/**
 * Owner onboarding steps:
 * 0 - Welcome modal (asks if user wants to proceed)
 * 1 - Mój pupil (My Dog) - highlight sidebar, explain adding a dog
 * 2 - Zapisy (Calendar) - highlight calendar, explain booking trainings
 * 3 - Moje konto (My Account) - highlight account page, explain profile editing
 */
export const OWNER_ONBOARDING_STEPS = {
    WELCOME: 0,
    MY_DOG: 1,
    CALENDAR: 2,
    MY_ACCOUNT: 3,
};

export const ONBOARDING_STEP_CONFIG = [
    {
        id: 'welcome',
        titleKey: 'onboarding:owner.welcome.title',
        descriptionKey: 'onboarding:owner.welcome.description',
        targetSelector: null, // No target for welcome modal
    },
    {
        id: 'my-dog',
        titleKey: 'onboarding:owner.myDog.title',
        descriptionKey: 'onboarding:owner.myDog.description',
        targetSelector: '[data-onboarding="my-dog"]',
        route: '/owner/my-dog',
    },
    {
        id: 'calendar',
        titleKey: 'onboarding:owner.calendar.title',
        descriptionKey: 'onboarding:owner.calendar.description',
        targetSelector: '[data-onboarding="calendar"]',
        route: '/owner/calendar',
    },
    {
        id: 'my-account',
        titleKey: 'onboarding:owner.account.title',
        descriptionKey: 'onboarding:owner.account.description',
        targetSelector: '[data-onboarding="my-account"]',
        route: '/owner/account',
    },
];

export const OnboardingProvider = ({ children }) => {
    // Get owner data from OwnerContext
    const ownerContext = useContext(OwnerContext);
    const owner = ownerContext?.owner;
    const ownerLoading = ownerContext?.loading;
    const refreshOwnerData = ownerContext?.refreshOwnerData;

    // Helper to get saved tour state from localStorage
    const getSavedState = () => {
        try {
            const saved = localStorage.getItem(ONBOARDING_STATE_KEY);
            if (saved) {
                return JSON.parse(saved);
            }
        } catch (e) {
            console.error('Error parsing onboarding state:', e);
        }
        return null;
    };

    // Onboarding completion status (from backend)
    const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(true); // Default to true until we know
    const [isInitialized, setIsInitialized] = useState(false);

    // Is the onboarding tour currently active (restore from localStorage)
    const [isOnboardingActive, setIsOnboardingActive] = useState(() => {
        const savedState = getSavedState();
        return savedState?.isActive || false;
    });

    // Current step in the onboarding flow (restore from localStorage)
    const [currentStep, setCurrentStep] = useState(() => {
        const savedState = getSavedState();
        return savedState?.currentStep || OWNER_ONBOARDING_STEPS.WELCOME;
    });

    // Should show the welcome modal
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);

    // API call state
    const [isUpdating, setIsUpdating] = useState(false);

    // Initialize from owner data when available (only once)
    useEffect(() => {
        // Only initialize once - don't re-initialize when owner data refreshes
        if (isInitialized) return;

        // Wait for owner data to be loaded (loading must be explicitly false, not undefined)
        const isLoading = ownerLoading === true;
        if (!isLoading && owner) {
            // Treat null, undefined, or false as "not completed"
            // Only true means completed
            const completed = owner.onboardingCompleted === true;
            setHasCompletedOnboarding(completed);
            setIsInitialized(true);
        }
    }, [owner, ownerLoading, isInitialized]);

    // Show welcome modal after initialization if onboarding not completed
    useEffect(() => {
        if (isInitialized && !hasCompletedOnboarding && !isOnboardingActive && !showWelcomeModal) {
            const timer = setTimeout(() => {
                setShowWelcomeModal(true);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [isInitialized, hasCompletedOnboarding, isOnboardingActive, showWelcomeModal]);

    // Persist tour state to localStorage when it changes
    useEffect(() => {
        if (isOnboardingActive) {
            localStorage.setItem(ONBOARDING_STATE_KEY, JSON.stringify({
                isActive: isOnboardingActive,
                currentStep: currentStep,
            }));
        } else {
            localStorage.removeItem(ONBOARDING_STATE_KEY);
        }
    }, [isOnboardingActive, currentStep]);

    /**
     * Mark onboarding as completed in the backend
     */
    const markOnboardingComplete = useCallback(async () => {
        try {
            setIsUpdating(true);
            const token = localStorage.getItem('jwt');
            await axios.put('/api/v1/owner/onboarding/complete', {}, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            setHasCompletedOnboarding(true);
            // Refresh owner data so the new status persists across page navigations
            if (refreshOwnerData) {
                await refreshOwnerData();
            }
        } catch (error) {
            console.error('Failed to update onboarding status:', error);
            // Still mark as completed locally even if API fails
            setHasCompletedOnboarding(true);
        } finally {
            setIsUpdating(false);
        }
    }, [refreshOwnerData]);

    /**
     * Reset onboarding in the backend (for "Moje konto" page)
     */
    const resetOnboardingInBackend = useCallback(async () => {
        try {
            setIsUpdating(true);
            const token = localStorage.getItem('jwt');
            await axios.put('/api/v1/owner/onboarding/reset', {}, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            setHasCompletedOnboarding(false);
            setIsOnboardingActive(false);
            setCurrentStep(OWNER_ONBOARDING_STEPS.WELCOME);
            setShowWelcomeModal(true);
            localStorage.removeItem(ONBOARDING_STATE_KEY);
            // Refresh owner data
            if (refreshOwnerData) {
                await refreshOwnerData();
            }
        } catch (error) {
            console.error('Failed to reset onboarding status:', error);
        } finally {
            setIsUpdating(false);
        }
    }, [refreshOwnerData]);

    /**
     * Start the onboarding tour from step 1 (after welcome modal)
     */
    const startOnboarding = useCallback(() => {
        setShowWelcomeModal(false);
        setIsOnboardingActive(true);
        setCurrentStep(OWNER_ONBOARDING_STEPS.MY_DOG);
    }, []);

    /**
     * Skip the onboarding and mark as completed
     */
    const skipOnboarding = useCallback(() => {
        setShowWelcomeModal(false);
        setIsOnboardingActive(false);
        markOnboardingComplete();
        localStorage.removeItem(ONBOARDING_STATE_KEY);
    }, [markOnboardingComplete]);

    /**
     * Complete the onboarding tour
     */
    const completeOnboarding = useCallback(() => {
        setIsOnboardingActive(false);
        setCurrentStep(OWNER_ONBOARDING_STEPS.WELCOME);
        markOnboardingComplete();
        localStorage.removeItem(ONBOARDING_STATE_KEY);
    }, [markOnboardingComplete]);

    /**
     * Move to the next step
     */
    const nextStep = useCallback(() => {
        const maxStep = ONBOARDING_STEP_CONFIG.length - 1;
        if (currentStep < maxStep) {
            setCurrentStep(prev => prev + 1);
        } else {
            // Complete onboarding if at last step
            completeOnboarding();
        }
    }, [currentStep, completeOnboarding]);

    /**
     * Move to the previous step
     */
    const prevStep = useCallback(() => {
        if (currentStep > OWNER_ONBOARDING_STEPS.MY_DOG) {
            setCurrentStep(prev => prev - 1);
        }
    }, [currentStep]);

    /**
     * Reset onboarding state (calls backend API)
     */
    const resetOnboarding = useCallback(() => {
        resetOnboardingInBackend();
    }, [resetOnboardingInBackend]);

    /**
     * Get current step configuration
     */
    const getCurrentStepConfig = useCallback(() => {
        const config = ONBOARDING_STEP_CONFIG[currentStep];
        if (!config) return null;
        return {
            ...config,
            title: i18n.t(config.titleKey),
            description: i18n.t(config.descriptionKey),
        };
    }, [currentStep]);

    /**
     * Check if we're on the last step
     */
    const isLastStep = currentStep === ONBOARDING_STEP_CONFIG.length - 1;

    /**
     * Check if we're on the first tour step (not welcome)
     */
    const isFirstTourStep = currentStep === OWNER_ONBOARDING_STEPS.MY_DOG;

    const value = {
        // State
        hasCompletedOnboarding,
        isOnboardingActive,
        currentStep,
        showWelcomeModal,
        isLastStep,
        isFirstTourStep,
        isInitialized,
        isUpdating,

        // Actions
        startOnboarding,
        skipOnboarding,
        nextStep,
        prevStep,
        completeOnboarding,
        resetOnboarding,

        // Helpers
        getCurrentStepConfig,
        totalSteps: ONBOARDING_STEP_CONFIG.length - 1, // Exclude welcome
    };

    return (
        <OnboardingContext.Provider value={value}>
            {children}
        </OnboardingContext.Provider>
    );
};

export const useOnboarding = () => {
    const context = useContext(OnboardingContext);
    if (!context) {
        throw new Error('useOnboarding must be used within an OnboardingProvider');
    }
    return context;
};

export default OnboardingContext;
